// @ts-nocheck
import { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Pressable,
  Platform,
  ActivityIndicator,
  TextInput,
  Linking,
} from 'react-native';
import { useRouter } from 'expo-router';
import {
  Users,
  Search,
  Phone,
  MessageCircle,
  ChevronLeft,
  Filter,
  Clock,
  CheckCircle,
  AlertCircle,
} from 'lucide-react-native';
import { theme } from '@/constants/theme';
import { supabase } from '@/utils/supabase';
import { Lead, Mentor } from '@/types/mentor';
import { AccessibilityButton } from '@/components/AccessibilityButton';
import { EmptyState } from '@/components/EmptyState';
import { SkeletonLeadCard } from '@/components/SkeletonLoader';

export default function MentorLeadsScreen() {
  const router = useRouter();
  const [mentor, setMentor] = useState<Mentor | null>(null);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [filteredLeads, setFilteredLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'contacted' | 'completed'>('all');

  useEffect(() => {
    loadLeads();
  }, []);

  useEffect(() => {
    filterLeads();
  }, [leads, searchQuery, statusFilter]);

  const loadLeads = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.email) {
        router.replace('/mentor/login');
        return;
      }

      const { data: mentorData } = await supabase
        .from('mentors')
        .select('*')
        .eq('email', user.email)
        .maybeSingle();

      if (mentorData) {
        setMentor(mentorData);
      }

      const { data: courses } = await supabase
        .from('processed_raw_courses')
        .select('course_id')
        .eq('mentor_email', user.email);

      if (!courses || courses.length === 0) {
        setLeads([]);
        setLoading(false);
        return;
      }

      const courseIds = courses.map(c => c.course_id);

      const { data: leadsData } = await supabase
        .from('leads')
        .select('*')
        .in('course_id', courseIds)
        .order('created_at', { ascending: false });

      if (leadsData) {
        setLeads(leadsData);
      }
    } catch (error) {
      console.error('Error loading leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterLeads = () => {
    let filtered = [...leads];

    if (statusFilter !== 'all') {
      filtered = filtered.filter((lead) => lead.status === statusFilter);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (lead) =>
          lead.full_name?.toLowerCase().includes(query) ||
          lead.phone?.includes(query) ||
          lead.course_id?.toLowerCase().includes(query)
      );
    }

    setFilteredLeads(filtered);
  };

  const handleCall = (phone: string) => {
    const formattedPhone = phone.replace(/[^0-9]/g, '');
    Linking.openURL(`tel:${formattedPhone}`);
  };

  const handleWhatsApp = (phone: string) => {
    const formattedPhone = phone.replace(/[^0-9]/g, '');
    const israelPhone = formattedPhone.startsWith('0')
      ? `972${formattedPhone.substring(1)}`
      : formattedPhone;
    Linking.openURL(`whatsapp://send?phone=${israelPhone}`);
  };

  const updateLeadStatus = async (leadId: string, newStatus: 'pending' | 'contacted' | 'completed') => {
    try {
      const { error } = await supabase
        .from('leads')
        .update({ status: newStatus })
        .eq('id', leadId);

      if (!error) {
        setLeads((prev) =>
          prev.map((lead) =>
            lead.id === leadId ? { ...lead, status: newStatus } : lead
          )
        );
      }
    } catch (error) {
      console.error('Error updating lead status:', error);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <AlertCircle color="#FFA500" size={20} />;
      case 'contacted':
        return <Clock color="#2196F3" size={20} />;
      case 'completed':
        return <CheckCircle color="#4CAF50" size={20} />;
      default:
        return null;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return 'ממתין';
      case 'contacted':
        return 'בטיפול';
      case 'completed':
        return 'הושלם';
      default:
        return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return '#FFA500';
      case 'contacted':
        return '#2196F3';
      case 'completed':
        return '#4CAF50';
      default:
        return theme.colors.textLight;
    }
  };

  if (loading) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <View style={styles.headerTop}>
            <Text style={styles.title}>ניהול לידים</Text>
            <Pressable onPress={() => router.back()} style={styles.backButton}>
              <ChevronLeft color={theme.colors.text} size={24} />
            </Pressable>
          </View>
        </View>
        <ScrollView style={styles.content} contentContainerStyle={styles.contentContainer}>
          <SkeletonLeadCard />
          <SkeletonLeadCard />
          <SkeletonLeadCard />
        </ScrollView>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View style={styles.headerTop}>
          <Text
            style={styles.title}
            accessible={true}
            accessibilityRole="header"
            {...(Platform.OS === 'web' && {
              role: 'heading',
              'aria-level': 1,
            } as any)}
          >
            ניהול לידים
          </Text>
          <Pressable
            onPress={() => router.back()}
            style={styles.backButton}
            accessible={true}
            accessibilityRole="button"
            accessibilityLabel="חזור לדאשבורד"
            {...(Platform.OS === 'web' && {
              'aria-label': 'חזור לדאשבורד',
            } as any)}
          >
            <ChevronLeft color={theme.colors.text} size={24} aria-hidden={true} />
          </Pressable>
        </View>

        <View style={styles.searchContainer}>
          <Search color={theme.colors.textLight} size={20} aria-hidden={true} />
          <TextInput
            style={styles.searchInput}
            value={searchQuery}
            onChangeText={setSearchQuery}
            placeholder="חיפוש לפי שם, טלפון או קורס..."
            placeholderTextColor={theme.colors.textLight}
            accessible={true}
            accessibilityLabel="חיפוש לידים"
            accessibilityHint="הקלד שם, טלפון או שם קורס לחיפוש"
          />
        </View>

        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.filterContainer}
        >
          <Pressable
            style={[
              styles.filterButton,
              statusFilter === 'all' && styles.filterButtonActive,
            ]}
            onPress={() => setStatusFilter('all')}
            accessible={true}
            accessibilityRole="button"
            accessibilityLabel={`הצג את כל הלידים, ${leads.length} לידים`}
            accessibilityState={{ selected: statusFilter === 'all' }}
            {...(Platform.OS === 'web' && {
              'aria-label': `הצג את כל הלידים, ${leads.length} לידים`,
            } as any)}
          >
            <Text
              style={[
                styles.filterButtonText,
                statusFilter === 'all' && styles.filterButtonTextActive,
              ]}
            >
              הכל ({leads.length})
            </Text>
          </Pressable>

          <Pressable
            style={[
              styles.filterButton,
              statusFilter === 'pending' && styles.filterButtonActive,
            ]}
            onPress={() => setStatusFilter('pending')}
            accessible={true}
            accessibilityRole="button"
            accessibilityLabel={`הצג לידים ממתינים, ${leads.filter((l) => l.status === 'pending').length} לידים`}
            accessibilityState={{ selected: statusFilter === 'pending' }}
            {...(Platform.OS === 'web' && {
              'aria-label': `הצג לידים ממתינים, ${leads.filter((l) => l.status === 'pending').length} לידים`,
            } as any)}
          >
            <Text
              style={[
                styles.filterButtonText,
                statusFilter === 'pending' && styles.filterButtonTextActive,
              ]}
            >
              ממתינים ({leads.filter((l) => l.status === 'pending').length})
            </Text>
          </Pressable>

          <Pressable
            style={[
              styles.filterButton,
              statusFilter === 'contacted' && styles.filterButtonActive,
            ]}
            onPress={() => setStatusFilter('contacted')}
            accessible={true}
            accessibilityRole="button"
            accessibilityLabel={`הצג לידים בטיפול, ${leads.filter((l) => l.status === 'contacted').length} לידים`}
            accessibilityState={{ selected: statusFilter === 'contacted' }}
            {...(Platform.OS === 'web' && {
              'aria-label': `הצג לידים בטיפול, ${leads.filter((l) => l.status === 'contacted').length} לידים`,
            } as any)}
          >
            <Text
              style={[
                styles.filterButtonText,
                statusFilter === 'contacted' && styles.filterButtonTextActive,
              ]}
            >
              בטיפול ({leads.filter((l) => l.status === 'contacted').length})
            </Text>
          </Pressable>

          <Pressable
            style={[
              styles.filterButton,
              statusFilter === 'completed' && styles.filterButtonActive,
            ]}
            onPress={() => setStatusFilter('completed')}
            accessible={true}
            accessibilityRole="button"
            accessibilityLabel={`הצג לידים שהושלמו, ${leads.filter((l) => l.status === 'completed').length} לידים`}
            accessibilityState={{ selected: statusFilter === 'completed' }}
            {...(Platform.OS === 'web' && {
              'aria-label': `הצג לידים שהושלמו, ${leads.filter((l) => l.status === 'completed').length} לידים`,
            } as any)}
          >
            <Text
              style={[
                styles.filterButtonText,
                statusFilter === 'completed' && styles.filterButtonTextActive,
              ]}
            >
              הושלמו ({leads.filter((l) => l.status === 'completed').length})
            </Text>
          </Pressable>
        </ScrollView>
      </View>

      <ScrollView
        style={styles.content}
        contentContainerStyle={styles.contentContainer}
        showsVerticalScrollIndicator={false}
      >
        {filteredLeads.length === 0 ? (
          <EmptyState
            icon={Users}
            title={searchQuery || statusFilter !== 'all' ? 'לא נמצאו תוצאות' : 'עדיין אין לידים'}
            description={
              searchQuery || statusFilter !== 'all'
                ? 'נסה לשנות את הפילטרים או את החיפוש'
                : 'לידים חדשים יופיעו כאן כשתלמידים יתעניינו בקורסים שלך'
            }
          />
        ) : (
          filteredLeads.map((lead) => (
            <Pressable
              key={lead.id}
              style={({ pressed }) => [
                styles.leadCard,
                pressed && styles.leadCardPressed,
              ]}
              onPress={() => router.push(`/mentor/leads/${lead.id}`)}
              accessible={true}
              accessibilityRole="button"
              accessibilityLabel={`ליד של ${lead.full_name}, קורס ${lead.course_id}, ${getStatusText(lead.status)}`}
              accessibilityHint="לחץ לצפייה בפרטים המלאים"
              {...(Platform.OS === 'web' && {
                'aria-label': `ליד של ${lead.full_name}, קורס ${lead.course_id}, ${getStatusText(lead.status)}`,
              } as any)}
            >
              <View style={styles.leadHeader}>
                <View style={styles.leadInfo}>
                  <Text style={styles.leadName}>{lead.full_name}</Text>
                  <Text style={styles.leadCourse}>קורס: {lead.course_id}</Text>
                </View>
                <View
                  style={[styles.statusBadge, { backgroundColor: `${getStatusColor(lead.status)}20` }]}
                  accessible={true}
                  accessibilityLabel={`סטטוס: ${getStatusText(lead.status)}`}
                  aria-hidden={true}
                >
                  {getStatusIcon(lead.status)}
                  <Text style={[styles.statusText, { color: getStatusColor(lead.status) }]}>
                    {getStatusText(lead.status)}
                  </Text>
                </View>
              </View>

              <View style={styles.leadDetails}>
                <Text style={styles.leadPhone}>{lead.phone}</Text>
                {lead.message && (
                  <Text style={styles.leadMeta} numberOfLines={2}>
                    {lead.message}
                  </Text>
                )}
                <Text style={styles.leadDate}>
                  {new Date(lead.created_at).toLocaleDateString('he-IL', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </Text>
              </View>

              <View style={styles.leadActions}>
                <Pressable
                  style={styles.actionButton}
                  onPress={(e) => {
                    e.stopPropagation();
                    handleCall(lead.phone);
                  }}
                  accessible={true}
                  accessibilityRole="button"
                  accessibilityLabel={`התקשר ל-${lead.full_name}`}
                  {...(Platform.OS === 'web' && {
                    'aria-label': `התקשר ל-${lead.full_name}`,
                  } as any)}
                >
                  <Phone color={theme.colors.primary} size={20} />
                  <Text style={styles.actionButtonText}>התקשר</Text>
                </Pressable>

                <Pressable
                  style={styles.actionButton}
                  onPress={(e) => {
                    e.stopPropagation();
                    handleWhatsApp(lead.phone);
                  }}
                  accessible={true}
                  accessibilityRole="button"
                  accessibilityLabel={`שלח הודעת WhatsApp ל-${lead.full_name}`}
                  {...(Platform.OS === 'web' && {
                    'aria-label': `שלח הודעת WhatsApp ל-${lead.full_name}`,
                  } as any)}
                >
                  <MessageCircle color="#25D366" size={20} />
                  <Text style={styles.actionButtonText}>WhatsApp</Text>
                </Pressable>

                {lead.status === 'pending' && (
                  <Pressable
                    style={styles.actionButton}
                    onPress={(e) => {
                      e.stopPropagation();
                      updateLeadStatus(lead.id, 'contacted');
                    }}
                    accessible={true}
                    accessibilityRole="button"
                    accessibilityLabel={`סמן את הליד של ${lead.full_name} כטופל`}
                    {...(Platform.OS === 'web' && {
                      'aria-label': `סמן את הליד של ${lead.full_name} כטופל`,
                    } as any)}
                  >
                    <CheckCircle color="#4CAF50" size={20} />
                    <Text style={styles.actionButtonText}>סמן כטופל</Text>
                  </Pressable>
                )}
              </View>
            </Pressable>
          ))
        )}
      </ScrollView>

      <AccessibilityButton />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  loadingContainer: {
    flex: 1,
    backgroundColor: theme.colors.background,
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.md,
  },
  loadingText: {
    fontSize: 16,
    fontFamily: theme.fonts.medium,
    color: theme.colors.textLight,
  },
  header: {
    backgroundColor: theme.colors.backgroundLight,
    paddingHorizontal: theme.spacing.lg,
    paddingTop: Platform.OS === 'ios' ? 50 : theme.spacing.md,
    paddingBottom: theme.spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
    gap: theme.spacing.md,
  },
  headerTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    fontSize: 24,
    fontFamily: theme.fonts.bold,
    color: theme.colors.text,
  },
  backButton: {
    padding: theme.spacing.xs,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.secondary,
    borderRadius: theme.borderRadius.lg,
    paddingHorizontal: theme.spacing.md,
    gap: theme.spacing.sm,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    fontFamily: theme.fonts.regular,
    color: theme.colors.text,
    paddingVertical: theme.spacing.sm,
    textAlign: 'right',
  },
  filterContainer: {
    flexDirection: 'row',
  },
  filterButton: {
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    borderRadius: theme.borderRadius.full,
    backgroundColor: theme.colors.secondary,
    marginLeft: theme.spacing.sm,
  },
  filterButtonActive: {
    backgroundColor: theme.colors.primary,
  },
  filterButtonText: {
    fontSize: 14,
    fontFamily: theme.fonts.medium,
    color: theme.colors.textLight,
  },
  filterButtonTextActive: {
    color: '#FAF5EA',
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    padding: theme.spacing.lg,
    gap: theme.spacing.md,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: theme.spacing.xl * 2,
    gap: theme.spacing.lg,
  },
  emptyStateText: {
    fontSize: 16,
    fontFamily: theme.fonts.medium,
    color: theme.colors.textLight,
  },
  leadCard: {
    backgroundColor: theme.colors.secondary,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.lg,
    borderWidth: 1,
    borderColor: theme.colors.border,
    gap: theme.spacing.md,
  },
  leadCardPressed: {
    transform: [{ scale: 0.98 }],
    opacity: 0.8,
  },
  leadHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  leadInfo: {
    flex: 1,
    gap: theme.spacing.xs,
  },
  leadName: {
    fontSize: 18,
    fontFamily: theme.fonts.bold,
    color: theme.colors.text,
  },
  leadCourse: {
    fontSize: 14,
    fontFamily: theme.fonts.regular,
    color: theme.colors.textLight,
  },
  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.xs,
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: theme.spacing.xs,
    borderRadius: theme.borderRadius.md,
  },
  statusText: {
    fontSize: 12,
    fontFamily: theme.fonts.bold,
  },
  leadDetails: {
    gap: 4,
  },
  leadPhone: {
    fontSize: 16,
    fontFamily: theme.fonts.medium,
    color: theme.colors.text,
  },
  leadMeta: {
    fontSize: 14,
    fontFamily: theme.fonts.regular,
    color: theme.colors.textLight,
  },
  leadDate: {
    fontSize: 12,
    fontFamily: theme.fonts.regular,
    color: theme.colors.textLight,
  },
  leadActions: {
    flexDirection: 'row',
    gap: theme.spacing.sm,
    paddingTop: theme.spacing.sm,
    borderTopWidth: 1,
    borderTopColor: theme.colors.border,
  },
  actionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.xs,
    backgroundColor: theme.colors.background,
    paddingVertical: theme.spacing.sm,
    borderRadius: theme.borderRadius.md,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  actionButtonText: {
    fontSize: 14,
    fontFamily: theme.fonts.medium,
    color: theme.colors.text,
  },
});
